import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import InputAdornment from '@mui/material/InputAdornment';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { grey, lightBlue } from '@mui/material/colors';

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual, isNull, isUndefined, size, replace, cloneDeep, isNil } from "lodash";

import companyService from "@plannel/services/data/company-service";
import rpPolicyService from "@plannel/services/rp/rp-policy-service"
import locationItemService from "@plannel/services/data/location-item-service";
import planConfigService from "@plannel/services/data/plan-config-service";

import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import RpBulkValueUpdateModal from "@plannel/pages/replenishment-plan/rp-modal/RpBulkValueUpdateModal";
import CycleCalendarRenderer from "@plannel/components/aggrid/CycleCalendarRenderer";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import DataState from "@plannel/components/aggrid/DataState"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DateCondition from "@plannel/components/DateCondition";
import { BulkValueUpdateButton, CalculationButton, SaveButton, SettingsButton, FilterButton } from "@plannel/components/ActionIconButton";

import debug from "@plannel/utils/debug-util";
import NumberUtil from "@plannel/utils/number-util";
import reduxUtil from "@plannel/utils/redux-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import RpUtil from "../utils/rp-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import GridDatePicker from "@plannel/components/GridDatePicker";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM } = CodeUtil.rpPolicyCd;
const RP_POLICY_LIST = [P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM];
const RP_FILTER_ITEMATTR = "RP_FILTER_ITEMATTR";
const EXCEL_EXPORT = "excelExport";
const { snackBarMessageColor } = ColorUtil;
const { salesRefPeriod } = RpUtil.configDefaultValue;
const { itemType } = CodeUtil;
const INV_CLASS_CD_LIST = CodeUtil.dpABCConfig;
const RpPolicy = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const CURRENT_DATE = new ZDate();
  const MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
  const MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, 0, 1);

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? null);
  const [onlyRpApplied, setOnlyRpApplied] = useState(reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "onlyRpApplied") ? reduxViewState.onlyRpApplied : true);
  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [backDropOpen, setBackDropOpen] = useState(false);

  const [datas, setDatas] = useState([]);
  const [csvColDef, setCsvColDef] = useState([]);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const colsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "colsHide") ? reduxViewState.colsHide : true;
  const itemAttrColsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "itemAttrColsHide") ? reduxViewState.itemAttrColsHide : true;

  const updateTargets = ["rpApplied", "ipFixedFlg", "orderMethod", "safetyStockQty", "cycleInvFlg", "opInvDays", "targetSupplyDays", "cycleInvQty", "otdLtD", "orderCalendar", "moq", "ioq"];
  const addedBulkUpdateTargets = [
      { headerName: "itemType", field: "itemType", width: 130, cellClass: 'stringType', cellEditor: "agRichSelectCellEditor", type: ["defaultValueParser"], cellEditorPopup: true,
        cellEditorParams: {
          values: [{ cd: "all", name: "all" }, ...Object.values(itemType).filter(itemType => !["RM", "SFG"].includes(itemType.cd))],
          valueFormatter: params => {
            if (!params.value) return t("all");
            return params.value.name ? t(params.value.name) : t("all");
          }
        },
        valueFormatter: params => {
          if (!params.value) return t("all");
          return params.value.name ? t(params.value.name) : t("all");
        }
      }
    ];
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);

  const periodForAvgSalesConfig = useRef({});
  const [period, setPeriod] = useState("");
  const [settingModalOpen, setSettingModalOpen] = useState(false);

  const START_DATE = null;
  const END_DATE = null;
  const initPeriod = { start: reduxViewState?.period?.start ? new ZDate(reduxViewState.period.start) : START_DATE, end: reduxViewState?.period?.end ? new ZDate(reduxViewState.period.end) : END_DATE };
  const [periodSearch, setPeriodSearch] = useState(initPeriod);
  const [attrColDefs, setAttrColDefs] = useState([]);
  const [attrLoading, setAttrLoading] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  const rpPolicyCondition = [
    { cd: "readyToSellinDt", name: "readyToSellinDt" },
    { cd: "endOfSalesDt", name: "endOfSalesDt" }
  ]

  const checkEmpty = value => {
    return isUndefined(value) || isEqual(value, "") || isNull(value);
  }

  const objectComparator = (value1, value2, property) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    if (val1 === val2) {
      return 0;
    } else if (val1 > val2) {
      return 1;
    }
    return -1;
  }

  const customComparator = (valueA, valueB) => {
    if (valueA === null && valueB === null) {
        return 0;
    } else if (valueA === null) {
        return 1;
    } else if (valueB === null) {
        return -1;
    } else {
        return valueA - valueB;
    }
  };

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.location?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }
    const value = param.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const percentNumberFormatter = (params, isExport = false) => {
    const { rpFlg } = params.data;
    const value = params.data[params.colDef.field];
    if (checkEmpty(value) || !rpFlg) {
      return "";
    }
    if (isExport) {
      return NumberUtil.formatNumber(value / 100, { maximumFractionDigits: 4, minimumFractionDigits: 4 }).replace(/,/g, "");
    }
    return NumberUtil.formatNumber(value / 100, { style: "percent", maximumFractionDigits: 1, minimumFractionDigits: 1 });
  }
  percentNumberFormatter.customType = EXCEL_EXPORT;

  const columnDefs = useMemo(() => {
    const kitRelatedColumns = ["moq", "ioq", "rpPolicyCd", "otdLtD", "replenishCalendar"]; 

    const dynamicEditable = params => {
      const { kittingFlg, itemType, rpFlg, rpPolicyCd } = params.data;
      const { colId } = params.column.getColDef();

      if (rpFlg) {
        if (kitRelatedColumns.includes(colId) && itemType === "KIT" && kittingFlg) {
          return false;
        }

        if (isEqual(colId, "replenishCalendar")) {
          if (isEqual(rpPolicyCd, Q_SYSTEM) || isEqual(rpPolicyCd, BASE_STOCK_SYSTEM)) {
            return false;
          }
        }
      } 

      return rpFlg
    };

    const dynamicCellStyle = params => {
      const cellStyle = {};
      const editableColumns = ["rpFlg", "ipFixedFlg", "roFlg", "kittingFlg", "invClassCd", "rpPolicyCd", "cycleInvFlg", "cycleInvQty", "safetyStockQty", "opInvDays", "targetSupplyDays", "otdLtDays", "replenishCalendar", "moq", "ioq", "orderCost", "invHoldCostPercent"];
      const { colId } = params.colDef;
      const { rpFlg, itemType, rpPolicyCd, kittingFlg } = params.data;
  
      if (editableColumns.includes(colId)) {
        cellStyle.background = lightBlue[50];
      }
  
      if (!rpFlg) {
        cellStyle.background = grey[300];
      }

      if (rpFlg && kitRelatedColumns.includes(colId) && kittingFlg) {
        if (itemType === "KIT") {
          cellStyle.background = grey[300];
        }
      }

      if (isEqual(colId, "replenishCalendar")) {
        if (isEqual(rpPolicyCd, Q_SYSTEM) || isEqual(rpPolicyCd, BASE_STOCK_SYSTEM)) {
          cellStyle.background = grey[300];
        }
      }
  
      return cellStyle;
    }
  
    const integerFormatter = params => {
      const { rpFlg } = params.node.data;
      if (checkEmpty(params.value) || !rpFlg) {
        return "";
      }
      return NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    }
  
    const decimalFormatter = (params, decimal) => {
      const { rpFlg } = params.node.data;
      if (checkEmpty(params.value) || !rpFlg) {
        return "";
      }
      return NumberUtil.formatNumberData(params, { maximumFractionDigits: decimal, minimumFractionDigits: decimal });
    }

    const attrParentOption = isEmpty(attrColDefs) ?  {} : { // attr 존재 시, category 에 detail header 에 정의한다.
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, reduxKey: "itemAttrColsHide", colsHide: itemAttrColsHide, 
        detailColumns: attrColDefs.map(attrColDef => {
          return attrColDef?.field
        })
      }
    }

    const itemTypeValueFormatter = params => {
      const { value } = params;

      return itemType?.[value] ? GridUtils.gridValueL10N(itemType[value]?.name) : value;
    };

    return [
      {
        headerName: "locName", colId: "locName", field: "location.name",
        type: ["nonEditableColumn"], width: 130, pinned: "left", lockPinned: true,
        comparator: (value1, value2) => objectComparator(value1, value2, "name"), filterType: "string"
      },
      {
        headerName: "itemCd", colId: "itemCd", field: "item.itemCd",
        type: ["nonEditableColumn"], width: 150, pinned: "left", lockPinned: true, cellClass: 'stringType',
        comparator: (value1, value2) => objectComparator(value1, value2, "itemCd"), filterType: "string"
      },
      {
        headerName: "itemType", colId: "itemType", field: "item.itemType", type: ["nonEditableColumn"],
        valueFormatter: itemTypeValueFormatter, width: 150, pinned: "left", lockPinned: true, cellClass: 'stringType', 
        filterType: "string", filterOption: Object.keys(itemType).filter(key => !["RM", "SFG"].includes(key)).map(key => key.toLowerCase())
      },
      {
        headerName: "itemName", colId: "itemName", field: "item.name",
        type: ["nonEditableColumn"], width: 220, pinned: "left", lockPinned: true,
        comparator: (value1, value2) => objectComparator(value1, value2, "name"),
        ...attrParentOption, filterType: "string"
      },
      ...attrColDefs.map(attrColDef => {
        const obj = attrColDef;
        obj.hide = itemAttrColsHide;
        return obj;
      }),
      {
        headerName: "category", colId: "categoryName", field: "itemHrchy.name",
        type: ["nonEditableColumn"], width: 200,
        comparator: (value1, value2) => objectComparator(value1, value2, "name"), filterType: "string"
      },
      { headerName: "rpApplied", colId: "rpFlg", field: "rpFlg", type: ["booleanColumn"], width: 100, cellStyle: dynamicCellStyle, filterType: "boolean" },
      { headerName: "ipFixedFlg", colId: "ipFixedFlg", field: "ipFixedFlg", type: ["booleanColumn"], width: 110, cellStyle: dynamicCellStyle, filterType: "boolean" },
      { headerName: "roFlg", colId: "roFlg", field: "roFlg", type: ["booleanColumn"], width: 135, cellStyle: dynamicCellStyle, filterType: "boolean" },
      { headerName: "kittingFlg", colId: "kittingFlg", field: "kittingFlg", type: ["booleanColumn"], width: 120, cellStyle: dynamicCellStyle, filterType: "boolean" },
      {
        headerName: "invClassCd", colId: "invClassCd", field: "invClassCd", type: ["nonEditableColumn"], minWidth: 60, width: 120,
        cellRenderer: AbcXyzChip, filterType: "string", filterOption: INV_CLASS_CD_LIST
      },
      {
        headerName: "orderMethod", colId: "rpPolicyCd", field: "rpPolicyCd", width: 150,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        valueFormatter: params => GridUtils.gridValueL10N(params.value),
        cellEditorParams: {
          values: RP_POLICY_LIST
        },
        cellStyle: dynamicCellStyle, editable: dynamicEditable,
        headerComponent: CustomDetailHeader,
        headerComponentParams: {
          viewName, colsHide,
          detailColumns: ["targetInvQty", "safetyStockQty", "opInvDays", "targetSupplyDays", "cycleInvFlg", "cycleInvQty", "salesQtyAvg", "otdLtDays", "originLocation", "replenishCalendar"]
        }, filterType: "string", filterOption: RP_POLICY_LIST
      },
      {
        headerName: "targetInvQty", colId: "targetInvQty", field: "targetInvQty", width: 120, hide: colsHide,
        type: ["numberColumn", "rightAligned", "nonEditableColumn"], headerClass: "header-color-01", cellStyle: dynamicCellStyle, sortable: false
      },
      { headerName: "cycleStockApplied", colId: "cycleInvFlg", field: "cycleInvFlg", type: ["booleanColumn"], headerClass: "header-color-01", width: 140, hide: colsHide, cellStyle: dynamicCellStyle, filterType: "boolean" },
      {
        headerName: "cycleInvQty", colId: "cycleInvQty", field: "cycleInvQty", width: 120, hide: colsHide,
        type: ["numberColumn", "rightAligned"], headerClass: "header-color-01", cellStyle: dynamicCellStyle, cellEditor: NumericEditor, filterType: "number"
      },
      {
        headerName: "safetyStockQty", colId: "safetyStockQty", field: "safetyStockQty", width: 120, hide: colsHide,
        type: ["numberColumn", "rightAligned"], headerClass: "header-color-01", cellStyle: dynamicCellStyle, cellEditor: NumericEditor, filterType: "number"
      },
      {
        headerName: "opInvDays", colId: "opInvDays", field: "opInvDays", width: 120, hide: colsHide,
        type: ["numberColumn", "rightAligned"], headerClass: "header-color-01", cellStyle: dynamicCellStyle, cellEditor: NumericEditor, filterType: "number"
      },
      {
        headerName: "targetSupplyDays", colId: "targetSupplyDays", field: "targetSupplyDays", width: 120, hide: colsHide,
        type: ["numberColumn", "rightAligned"], headerClass: "header-color-01", cellStyle: dynamicCellStyle, cellEditor: NumericEditor, filterType: "number"
      },
      {
        headerName: "avgSalesQtyPerDay", colId: "salesQtyAvg", field: "salesQtyAvg",
        type: ["numberColumn", "rightAligned", "nonEditableColumn"], valueFormatter: params => decimalFormatter(params, 2),
        width: 120, hide: colsHide, headerClass: "header-color-01", cellStyle: dynamicCellStyle, sortable: false
      },
      {
        headerName: "otdLtD", colId: "otdLtDays", field: "otdLtDays", width: 120, hide: colsHide,
        type: ["rationalColumn", "rightAligned"], headerClass: "header-color-01", cellStyle: dynamicCellStyle, editable: dynamicEditable, filterType: "number"
      },
      {
        headerName: "suppliedBy", colId: "originLocation", field: "originLocation.name", width: 120, hide: colsHide,
        headerClass: "header-color-01", cellStyle: dynamicCellStyle, type: ["nonEditableColumn"], sortable: false
      },
      {
        headerName: "orderCalendar", colId: "replenishCalendar", field: "replenishCalendar", type: "nonEditableColumn",
        width: 140, hide: colsHide, cellStyle: dynamicCellStyle, headerClass: "header-color-01",
        comparator: (value1, value2) => objectComparator(value1, value2, "name"),
        valueGetter: params => {
          const { data } = params;
          const value = data.replenishCalendar
          return value?.name ? value.name : t("notConfigured");
        },
        cellRenderer: CycleCalendarRenderer, cellRendererParams: { setSnackInfo, editable: dynamicEditable }, sortable: false
      },
      {
        headerName: "moq", colId: "moq", field: "moq", width: 100,
        type: ["numberColumn", "rightAligned"], valueFormatter: integerFormatter, cellStyle: dynamicCellStyle, editable: dynamicEditable, filterType: "number"
      },
      {
        headerName: "ioq", colId: "ioq", field: "ioq", width: 100,
        type: ["numberColumn", "rightAligned"], valueFormatter: integerFormatter, cellStyle: dynamicCellStyle, editable: dynamicEditable, filterType: "number"
      },
      {
        headerName: "eoq", colId: "eoq", field: "eoq", width: 100,
        type: ["numberColumn", "rightAligned", "nonEditableColumn"], valueFormatter: integerFormatter, cellStyle: dynamicCellStyle, filterType: "number"
      },
      { headerName: "readyToSellinDt", field: "readyToSellinDt", type: ["nonEditableColumn"], comparator: (value1, value2) => customComparator(value1, value2), cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 135, filterType: "date" },
      { headerName: "endOfSalesDt", field: "endOfSalesDt", type: ["nonEditableColumn"], comparator: (value1, value2) => customComparator(value1, value2), cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 140, filterType: "date" },
      {
        headerName: "orderCost", colId: "orderCost", field: "orderCost", width: 110,
        type: ["numberColumn", "rightAligned"], valueFormatter: currencyRenderer, cellStyle: dynamicCellStyle, editable: dynamicEditable, filterType: "number"
      },
      {
        headerName: "invUnitCost", colId: "invUnitCost", field: "invUnitCost", width: 110,
        type: ["numberColumn", "rightAligned", "nonEditableColumn"], valueFormatter: currencyRenderer, cellStyle: dynamicCellStyle, sortable: false
      },
      {
        headerName: "invHoldCostRatio", colId: "invHoldCostPercent", field: "invHoldCostPercent", width: 140,
        type: ["numberColumn", "rightAligned"], valueFormatter: percentNumberFormatter, cellStyle: dynamicCellStyle, editable: dynamicEditable, filterType: "percent"
      },
      { colId: "invHoldCostRatio", field: "invHoldCostRatio", hide: true, suppressColumnsToolPanel: true }
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attrColDefs, colsHide, itemAttrColsHide]);

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = { currentDate: new ZDate(), onlyRpApplied };

    if (filterValue?.category) {
      searchFilters.categoryCd = filterValue?.category?.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (filterValue?.locations) {
      searchFilters.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (!isEmpty(invClassCdList)) {
      searchFilters.invClassCdList = invClassCdList;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (selectedSearch) {
      searchFilters.condition = selectedSearch.cd;
    }

    if (periodSearch?.start) {
      searchFilters.startDate = periodSearch?.start;
    }

    if (periodSearch?.end) {
      searchFilters.endDate = periodSearch?.end;
    }
    
    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }
    
    params.searchFilters = searchFilters;

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
          params.advancedFilters = advancedFilters;
    }

    return { ...params, ...sortParams };
  }

  const retrieveRpPolicy = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    rpPolicyService.getAll(params)
      .then(response => {
        const { results, totalPages, message, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        if (message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t(message) });
        }
      
        setDatas(results);
        isEmpty(results) ? setGridPages(0) : setGridPages(totalPages);
        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, onlyRpApplied, invClassCdList, currentPage, pageSize, selectedSearch, period: periodSearch, advancedFilters });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const saveRpPolicy = () => {
    gridRef.current.api.stopEditing(false);
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      rpPolicyService.update(allStateData)
        .then(response => {
          const { count, message } = response.data;
          if (message) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(message) });
          }
          if (count > 0) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          }
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          retrieveRpPolicy();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const onCellValueChanged = params => {
    const { colDef, node, value, oldValue } = params;
    const { colId } = colDef;

    if ((isUndefined(value) || isEqual(value, "")) && isNull(oldValue)) {
      node.data[colId] = null;

      gridRef.current.api.refreshCells({ force: true, columns: [colId], rowNodes: [node] });
    } else {
      if (["orderCost", "invHoldCostPercent"].includes(colId)) {
        const nodeData = node.data;

        if (isEqual(colId, "invHoldCostPercent")) {
          nodeData.invHoldCostRatio = Number(value) / 100;
        } else {
          nodeData.orderCost = Number(value);
        }

        if (!checkEmpty(nodeData.salesQtyAvg) && !checkEmpty(nodeData.orderCost) && !checkEmpty(nodeData.invUnitCost) && !checkEmpty(nodeData.invHoldCostRatio)) {
          const eoq = Math.sqrt(2 * nodeData.salesQtyAvg * 12 * nodeData.orderCost / (nodeData.invUnitCost * nodeData.invHoldCostRatio));
          nodeData.eoq = Math.round(eoq);
        }

        gridRef.current.api.redrawRows({ rowNodes: [node] });
        setTimeout(() => { gridRef.current.api.flashCells({ rowNodes: [node], columns: ["eoq"], fadeDelay: 1000 }); }, 300);
      } else if (["safetyStockQty", "cycleInvQty"].includes(colId)) {
        const { safetyStockQty, cycleInvQty } = node.data;

        const safetyQty = safetyStockQty ? Number(safetyStockQty) : 0;
        const cycleQty = cycleInvQty ? Number(cycleInvQty) : 0;
        const targetInvQty = safetyQty + cycleQty;

        node.setDataValue("targetInvQty", targetInvQty);
        setTimeout(() => { gridRef.current.api.flashCells({ rowNodes: [node], columns: ["targetInvQty"], fadeDelay: 1000 }); }, 300);
      }

      defaultGridMemo.onCellValueChanged(params);
    }
  }

  const recalculationEoq = () => {
    setBackDropOpen(true);
    rpPolicyService.recalculationEoq(new ZDate())
      .then(response => {
        const { count, message } = response.data;
        if (message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(message) });
        }
        if (count > 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_ApplySuccess") });
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        retrieveRpPolicy();
        setBackDropOpen(false);
      });
  }

  const retrievePeriodForAvgSales = () => {
    planConfigService.getConfigCd({ moduleCd: CodeUtil.module.RP, configCd: "SALES_REF_PERIOD" })
      .then(response => {
        const { numVal } = response.data;
        let value = salesRefPeriod;

        if (!isNil(numVal)) {
          value = Math.abs(Number(numVal));
          periodForAvgSalesConfig.current = { ...response.data, numVal: value };
        }

        const refPeriod = value ? Math.abs(Number(value)) : 0;
        setPeriod(refPeriod);
      })
      .catch(e => {
        debug(e);
      });
  }

  const checkValidation = numVal => {
    return isNaN(numVal) || isEqual(numVal, "") || !Number.isInteger(Number(numVal));
  }

  const savePeriodForAvgSales = () => {
    if (checkValidation(period)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterNumber") });
      return;
    }

    const basicConfig = { moduleCd: CodeUtil.module.RP, configCd: "SALES_REF_PERIOD", activeFlg: true };
    const upsertPlanConfig = { ...periodForAvgSalesConfig.current, ...basicConfig, numVal: -Number(period) };
    planConfigService.upsert([upsertPlanConfig])
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        setSettingModalOpen(false);

        retrievePeriodForAvgSales();
        retrieveRpPolicy();
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveRpPolicy();
    } else if (action === "RE_CALC_EOQ") {
      recalculationEoq();
    }
  }

  const handleClose = action => {
    if (isEqual(action, "cancel")) {
      setSettingModalOpen(false);
    } else if (isEqual(action, "save")) {
      savePeriodForAvgSales();
    }
  }

  const retrieveItemAttrData = () => {
    let customItemAttrDatas = [];
    const colDefs = [];

    const itemAttrReduxState = reduxUtil.getViewState(RP_FILTER_ITEMATTR);

    if (itemAttrReduxState?.itemAttr) {
      customItemAttrDatas = itemAttrReduxState.itemAttr;
                
      if (!isEmpty(customItemAttrDatas)) {
       customItemAttrDatas.forEach(attrData => {
          if (attrData.column) {
            if (attrData.column === "itemHrchy") {
              attrData.column = "itemHrchy01";
            }
            colDefs.push({ headerName: attrData.name, colId: attrData.column, field: attrData.column, filterName: attrData.column, type: ["nonEditableColumn"], headerClass: "header-color-01", comparator: (value1, value2) => customComparator(value1, value2), hide: itemAttrColsHide, width: 100, filterType: "string" });
          }
        });
      }
      setAttrColDefs(colDefs);
      setAttrLoading(true);
    } else {
      companyService.getCustomConfig()
        .then(response => {
          const { item } = response.data;
          if (item) {
            Object.keys(item).forEach(key => { 
              const itemAttrObj = {}
              itemAttrObj.column = key;
              itemAttrObj.name = item[key];

              customItemAttrDatas.push(itemAttrObj);
            });
                      
            if (!isEmpty(customItemAttrDatas)) {
              customItemAttrDatas.forEach(attrData => {
                if (attrData.column) {
                  if (attrData.column === "itemHrchy") {
                    attrData.column = "itemHrchy01";
                  }
                  colDefs.push({ headerName: attrData.name, colId: attrData.column, field: attrData.column, filterName: attrData.column, type: ["nonEditableColumn"], headerClass: "header-color-01", comparator: (value1, value2) => customComparator(value1, value2), hide: itemAttrColsHide, width: 100, filterType: "string" });
                }
              });
            }
            
            setAttrColDefs(colDefs);
          }
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          setAttrLoading(true);
          const itemAttrReduxState = reduxUtil.createObjectState(RP_FILTER_ITEMATTR, { itemAttr: customItemAttrDatas });
          reduxDispatch(reduxUtil.updateViewState(itemAttrReduxState));
        });
    }
  }

  const onGridReady = () => {
    retrievePeriodForAvgSales();
    retrieveItemAttrData();

    setGridLoading(true);
  }

  const handleCheckbox = e => {
    const value = e.target.checked;
    setOnlyRpApplied(value);
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveRpPolicy();
    }
  }

  const getCsvColDefs = () => {
    if (isEmpty(csvColDef)) {
      return columnDefs;
    }
 
    return csvColDef;
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading && attrLoading) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
      const filteredColumnDefs = cloneDeep(columnDefs).filter(colDef => colDef.field !== "invHoldCostRatio");
      setCsvColDef(filteredColumnDefs)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs, gridLoading, attrLoading]);

  useEffect(() => {
    if (!settingModalOpen) {
      if (periodForAvgSalesConfig.current?.numVal) {
        setPeriod(periodForAvgSalesConfig.current.numVal);
      }
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingModalOpen]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveRpPolicy();
    }

    return () => {
      setDatas([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setDatas([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue?.locations, filterValue?.category, onlyRpApplied, periodSearch, selectedSearch, invClassCdList, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, attrColDefs])


  const inputPropsByValue = value => {
    return value ? { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>{t("months")}</InputAdornment> } : {};
  }

  return (
    <>
      <FilterContainer>
        <LocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue}  enableMultiSelect selectLeafs/>
        <AbcXyzFilterModal invClassCdList={invClassCdList} setInvClassCdList={setInvClassCdList} />
        <RPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} sx={{ mr: 1 }} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <DateCondition
          conditionList={rpPolicyCondition}
          period={periodSearch}
          maxDate={MAX_DATE}
          minDate={MIN_DATE}
          selectedSearch={selectedSearch}
          views={['year', 'month', 'day']}
          dateFormat={'yyyy-MM-dd'}
          useAutoComplete
          setSelectedSearch={setSelectedSearch}
          setPeriod={setPeriodSearch}
          sx={{ mt: 0 }}
        />
        <FormControlLabel
                control={<Checkbox color="primary" name={"onlyRpApplied"} checked={onlyRpApplied} onChange={handleCheckbox} />}
                label={t("rpOnly")} key="ONLY_RP" sx={{ display: "flex" }} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={rpPolicyService} columnDefs={getCsvColDefs()} params={getRequestParams(false)} />
          <CalculationButton label="reCalcEoq" onClick={() => setDialogInfo({ open: true, actionName: "RE_CALC_EOQ", title: "reCalcEoq", content: t("MSG_ReCalcEoq") })} />
          <BulkValueUpdateButton onClick={() => setBulkUpdateModalOpen(true)} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <SettingsButton onClick={() => setSettingModalOpen(true)} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="rp-settings" className="ag-theme-balham grid-area" sx={{ position: 'relative', height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column', mb: 2 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <AgGridReact
              ref={gridRef}
              rowData={datas}
              {...defaultGridMemo}
              enableRangeSelection
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <>
              <ResizableFilterPanel>
                <AdvancedFilter
                  columnDefs={columnDefs}
                  advancedFilters={advancedFilters}
                  onApply={setAdvancedFilters}
                  onClose={() => setOpenFilter(false)}
                />
              </ResizableFilterPanel>
            </>
          )}
        </Box>
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: 1400 }} open={backDropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <RpBulkValueUpdateModal
        t={t}
        addedTargetColumnDefs={addedBulkUpdateTargets}
        updateTargets={updateTargets}
        service={locationItemService}
        modalOpen={bulkUpdateModalOpen}
        setModalOpen={setBulkUpdateModalOpen}
        setSnackInfo={setSnackInfo}
        setProgressOn={setBackDropOpen}
        retrieve={retrieveRpPolicy}
        moduleCd={CodeUtil.module.RP}
        useSiteAccess={true}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <BaseConfigModal
        open={settingModalOpen}
        onClose={() => handleClose("cancel")}
        title={t("setRefPeriodForAvgSales")}
        onSave={() => handleClose("save")}
        width={432}>
          <Box sx={{ display: "flex" }}>
            <TextField
              size="small"
              name="period"
              label={t("period")}
              value={period}
              sx={{ maxWidth: 200 }}
              required
              error={checkValidation(period)}
              InputProps={inputPropsByValue(period)}
              onChange={e => setPeriod(e.target.value)}
            />
          </Box>
      </BaseConfigModal>
    </>
  );
};

export default withTranslation()(RpPolicy);